import { React, connect, fetchApi } from '@gasbuddy/react';
import JSONTree from 'react-json-tree';
import { Dimmer, Loader, Segment, Button, Form, Divider, Message, Grid, Statistic, List } from 'semantic-ui-react';
import { Role } from '../../roles';
import { Auth, Nav } from '../../components';

function renderSection(name, records) {
  if (records?.length) {
    return (
      <div>
        <Divider />
        <h2>{name}</h2>
        <List>
          {records.map(r => (<List.Item><JSONTree hideRoot data={r} /></List.Item>))}
        </List>
      </div>
    );
  }
  return null;
}

function renderOutputs({ created, unchanged, skipped, updated } = {}) {
  return (
    <React.Fragment>
      {renderSection('Created', created)}
      {renderSection('Updated', updated)}
      {renderSection('Unchanged', unchanged)}
      {renderSection('Skipped', skipped)}
    </React.Fragment>
  );
}

class BaseManualGasBack extends React.Component {
  state = {}

  go = (e) => {
    e.preventDefault();
    const { offerId, partner, dryRun, csv } = this.state;
    this.setState({ loading: true, outputs: null }, async () => {
      const { body, status } = await fetchApi({
        url: '/payments/gasback/manual-gasback',
        body: { offerId, partner, dryRun, csv },
      }).catch(v => ({ body: v, status: 500 }));
      this.setState({
        loading: false,
        outputs: status === 200 ? body : null,
        error: status === 200 ? null : JSON.stringify(body, null, '\t'),
      });
    });
  }

  render() {
    const { loading, outputs, error } = this.state;
    return (
      <Auth
        forRoles={[Role.ManualGasBackSync, Role.Superuser]}
      >
        <Nav>
          <Dimmer.Dimmable as={Segment} dimmed={loading}>
            <Dimmer active={loading}>
              <Loader />
            </Dimmer>
            <div>
              <h1>Manually Create Partner GasBack</h1>
              <p>
                This page is used to manually create GasBack for partners who do not use
                an affiliate provider such as Impact Radius or Commission Junction.
                It will award real money, so be careful. You must prepare a CSV file that has,
                at the very least, a partner transaction id, GasBuddy account id (either as a real account id or
                as a &quot;partner specific&quot; encrypted account id), and an amount.
              </p>
              <h2>Fields</h2>
              <dl>
                <dt>date</dt>
                <dd>The date the partner transaction was made, in ISO format. Defaults to now.</dd>

                <dt>encrypted_account_id</dt>
                <dd>The partner specific account identifier - typically what was passed to the partner as the tracking value</dd>

                <dt>account_id</dt>
                <dd>
                  If encrypted_account_id is NOT provided, you must provide account_id,
                  which is the raw GasBuddy identity account_id (e.g. NOT the legacy MemberId)
                </dd>

                <dt>partner_transaction_id</dt>
                <dd>The unique identifier for the transaction between the partner and the GasBuddy account holder</dd>

                <dt>commission_amount</dt>
                <dd>
                  The amount of commission paid to GasBuddy for this transaction
                  (optional - you can pass sale_amount, commission_amount, or amount, depending on the way the offer is setup)
                </dd>

                <dt>sale_amount</dt>
                <dd>
                  The total amount of the sale (optional - you can pass sale_amount,
                  commission_amount, or amount, depending on the way the offer is setup)
                </dd>

                <dt>amount</dt>
                <dd>The amount of GasBack that should be awarded - overrides the commissioning logic in the GasBuddy database</dd>

                <dt>offer_id</dt>
                <dd>The GasBuddy numeric offer id that this redemption is under</dd>
              </dl>
              <Divider />
              <Form onSubmit={this.go}>
                {error
                  && (
                    <Message negative>
                      <Message.Content><pre>{error}</pre></Message.Content>
                    </Message>
                  )
                }
                <Form.Group inline>
                  <Form.Input
                    label="Partner/Merchant Name"
                    onChange={({ target: { value } }) => this.setState({ partner: value })}
                  />
                  <Form.Input
                    type="number"
                    label="Offer Id"
                    labelPosition="left"
                    onChange={({ target: { value } }) => this.setState({ offerId: value })}
                  />
                  <Form.Checkbox
                    label="Dry Run (do not create GasBack, just return what would happen)"
                    onChange={(d, v) => this.setState({ dryRun: v.checked })}
                  />
                </Form.Group>
                <Divider />
                <Form.Group widths="equal">
                  <Form.TextArea
                    rows={15}
                    label="CSV with Header Row"
                    onChange={(e, { value }) => this.setState({ csv: value })}
                  />
                </Form.Group>
                <Button loading={loading}>Go</Button>
              </Form>
              <Divider />
              {outputs && (
                <Grid padded columns="equal" style={{ textAlign: 'center' }}>
                  <Grid.Row>
                    <Grid.Column>
                      <Statistic>
                        <Statistic.Value>{outputs.created?.length}</Statistic.Value>
                        <Statistic.Label>Created</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic>
                        <Statistic.Value>{outputs.updated?.length}</Statistic.Value>
                        <Statistic.Label>Updated</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic>
                        <Statistic.Value>{outputs.skipped?.length}</Statistic.Value>
                        <Statistic.Label>Skipped</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                    <Grid.Column>
                      <Statistic>
                        <Statistic.Value>{outputs.unchanged?.length}</Statistic.Value>
                        <Statistic.Label>Unchanged</Statistic.Label>
                      </Statistic>
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              )}
              {outputs && renderOutputs(outputs)}
            </div>
          </Dimmer.Dimmable>
        </Nav>
      </Auth>
    );
  }
}

export const ManualGasBack = connect(({ api }) => ({ api }))(BaseManualGasBack);
